import { ComponentProps } from 'react';
import { BREAKPOINTS_WIDTHS, useWindowSize } from '@belong/common';
import { Divider, Tabs, Text } from '@belong/ui';
import clsx from 'clsx';
import { AgreeToTerms } from 'src/features/auth/components/agree-to-terms/agree-to-terms';
import { ExternalLogin } from 'src/features/auth/components/external-login/external-login';
import { RegionPhoneNumber } from 'src/features/auth/components/region-phone-number/region-phone-number';
import { SignInForm } from 'src/features/auth/components/sign-in-form/sign-in-form';
import { AUTH_STRINGS } from 'src/features/auth/strings';
import css from './sign-in-tab-panel.module.css';

type Props = {
  enforcedEmail?: string;
  defaultEmailValue?: ComponentProps<typeof SignInForm>['defaultEmailValue'];
  onError?:
    | ComponentProps<typeof ExternalLogin>['onError']
    | ComponentProps<typeof SignInForm>['onError'];
  onExternalLoginSuccess?: ComponentProps<typeof ExternalLogin>['onSuccess'];
  onExternalLoginSuccessWithoutPhoneOrEmail: ComponentProps<
    typeof ExternalLogin
  >['onSuccessWithoutPhoneOrEmail'];
  onFinish:
    | ComponentProps<typeof SignInForm>['onFinish']
    | ComponentProps<typeof ExternalLogin>['onFinish'];
  onSignInSuccess: ComponentProps<typeof SignInForm>['onSuccess'];
  onStart:
    | ComponentProps<typeof SignInForm>['onStart']
    | ComponentProps<typeof ExternalLogin>['onStart'];
};

export function SignInTabPanel({
  enforcedEmail,
  defaultEmailValue,
  onError = () => {},
  onExternalLoginSuccess = async () => {},
  onExternalLoginSuccessWithoutPhoneOrEmail = async () => {},
  onFinish = async () => {},
  onSignInSuccess = async () => {},
  onStart = () => {},
}: Props) {
  const { width } = useWindowSize();

  const isMobile = width < BREAKPOINTS_WIDTHS.SM;
  const shouldShowExternalLogin = !enforcedEmail;

  return (
    <Tabs.PanelItem>
      <div className="mb-2xl">
        <div className="sm:mt-sm sm:flex sm:justify-center">
          {shouldShowExternalLogin && (
            <>
              <div
                className={clsx(
                  'my-sm sm:my-xs sm:flex sm:flex-col sm:items-center sm:justify-center',
                  css.panel,
                )}
              >
                <ExternalLogin
                  onError={onError}
                  onFinish={onFinish}
                  onStart={onStart}
                  onSuccess={onExternalLoginSuccess}
                  onSuccessWithoutPhoneOrEmail={
                    onExternalLoginSuccessWithoutPhoneOrEmail
                  }
                />
                <div className="mt-lg hidden text-center sm:block">
                  <AgreeToTerms />
                </div>
              </div>
              <div
                className={clsx('mb-xs mt-lg px-lg w-full sm:m-0 sm:w-auto')}
              >
                <Divider
                  className={clsx(!isMobile && css.divider)}
                  direction={isMobile ? 'horizontal' : 'vertical'}
                >
                  <div className="mx-xs sm:my-xs">
                    <Text className="text-gray" fontWeight="semibold">
                      {AUTH_STRINGS['or']}
                    </Text>
                  </div>
                </Divider>
              </div>
            </>
          )}
          <div className={clsx('sm:flex sm:items-center', css.panel)}>
            <SignInForm
              // These two props are the union of what both children declare,
              // and only this component's own member fits here. The page
              // passes one handler for both, so the casts are what that
              // arrangement costs.
              onError={onError as ComponentProps<typeof SignInForm>['onError']}
              onFinish={
                onFinish as ComponentProps<typeof SignInForm>['onFinish']
              }
              onStart={onStart}
              onSuccess={onSignInSuccess}
              defaultEmailValue={defaultEmailValue}
              enforcedEmail={enforcedEmail}
            />
          </div>
        </div>
        <div className="my-lg text-center sm:hidden">
          <AgreeToTerms />
        </div>
        <div className="sm:mt-lg flex w-full items-center justify-center">
          <RegionPhoneNumber />
        </div>
      </div>
    </Tabs.PanelItem>
  );
}
